import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import LangSwitcher from "../components/LangSwitcher";
import {
  Mic,
  FileText,
  Building2,
  ShieldCheck,
  ArrowRight,
  Zap,
  Cpu,
  LayoutDashboard,
} from "lucide-react";
import { motion } from "framer-motion";

export default function Landing() {
  const { t, lang } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const isLogged = !!user && !authLoading;

  const headline = t("landing.headline") || "";
  const headlineParts = headline.split(".");
  const headlineMain = headlineParts[0] ? `${headlineParts[0]}.` : "";
  const headlineAccent = headlineParts[1] ? `${headlineParts[1].trim()}.` : "";

  const goAppLabel = lang === "es" ? "Ir a la app" : "Vés a l'app";

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute inset-0 noise-overlay" />

      {/* Header */}
      <header className="relative z-20 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto h-14 sm:h-16 px-3 sm:px-5 lg:px-8 flex items-center justify-between gap-2">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src="/logo-hosys.png" alt="HO-SYS"
                 className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg object-cover shadow-[0_0_20px_rgba(59,130,246,0.4)]" />
            <span className="font-display font-bold tracking-tight text-base sm:text-lg">
              HO-SYS
            </span>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-3 min-w-0">
            <LangSwitcher />
            {isLogged ? (
              <Link to="/app">
                <Button
                  size="sm"
                  data-testid="header-go-app-btn"
                  className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.3)] px-3 sm:px-4 h-9 text-xs sm:text-sm whitespace-nowrap"
                >
                  <LayoutDashboard size={14} className="mr-1.5 hidden sm:inline" />
                  {goAppLabel}
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="hidden xs:block sm:block">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="header-login-btn"
                    className="text-slate-300 hover:text-white hover:bg-white/5 px-2 sm:px-4 h-9"
                  >
                    {t("auth.login")}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button
                    size="sm"
                    data-testid="header-register-btn"
                    className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.3)] px-3 sm:px-4 h-9 text-xs sm:text-sm whitespace-nowrap"
                  >
                    {t("landing.cta_primary")}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 pt-10 sm:pt-16 lg:pt-20 pb-12 sm:pb-20 lg:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] sm:text-xs border border-blue-500/30 bg-blue-500/10 text-blue-300 mb-5 sm:mb-6">
            <Mic size={12} /> {t("landing.tag")}
          </span>
          <h1 className="text-3xl xs:text-4xl sm:text-5xl lg:text-7xl font-display font-extrabold tracking-tight sm:tracking-tighter leading-[1.1] text-white break-words">
            {headlineMain}{" "}
            <span className="gradient-text">{headlineAccent}</span>
          </h1>
          <p className="mt-4 sm:mt-6 text-sm sm:text-base lg:text-lg text-slate-400 max-w-2xl leading-relaxed">
            {t("landing.sub")}
          </p>
          <div className="mt-7 sm:mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
            <Link to={isLogged ? "/app" : "/register"}>
              <Button
                data-testid="hero-cta-primary"
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-[0_0_30px_rgba(59,130,246,0.4)] px-6 sm:px-8 h-11 sm:h-12"
              >
                {isLogged ? goAppLabel : t("landing.cta_primary")}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <a href="#features">
              <Button
                variant="outline"
                size="lg"
                data-testid="hero-cta-secondary"
                className="border-white/15 bg-white/5 hover:bg-white/10 h-11 sm:h-12"
              >
                {t("landing.cta_secondary")}
              </Button>
            </a>
          </div>
        </motion.div>

        {/* Hero visual card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-12 sm:mt-20 relative"
        >
          <div className="absolute -inset-1 bg-gradient-to-r from-blue-500/30 via-indigo-500/20 to-blue-500/30 rounded-3xl blur-2xl" />
          <div className="relative card-surface p-4 sm:p-6 lg:p-8 rounded-2xl sm:rounded-3xl shimmer-border">
            <div className="grid md:grid-cols-2 gap-5 sm:gap-8 items-center">
              <div className="min-w-0">
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-blue-300 mb-2 sm:mb-3">
                  {t("landing.demo_label")}
                </div>
                <p className="font-display text-lg sm:text-2xl lg:text-3xl text-white leading-tight break-words">
                  "{t("landing.demo_quote_prefix")}{" "}
                  <span className="text-blue-300">
                    {t("landing.demo_quote_client")}
                  </span>{" "}
                  {t("landing.demo_quote_amount")}{" "}
                  {t("landing.demo_quote_suffix")}"
                </p>
                <div className="mt-4 sm:mt-6 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs sm:text-sm text-slate-400">
                  <span className="inline-flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-emerald-400 animate-pulse" />
                    1.4s
                  </span>
                  <span className="hidden sm:inline">·</span>
                  <span>{t("landing.demo_lines")}</span>
                  <span className="hidden sm:inline">·</span>
                  <span>{t("landing.demo_iva")}</span>
                </div>
              </div>

              <div className="bg-[#05070D] rounded-xl sm:rounded-2xl border border-white/10 p-3 sm:p-5 font-mono text-[10px] sm:text-xs min-w-0">
                <div className="flex items-center gap-1.5 sm:gap-2 mb-2 sm:mb-3 text-slate-500">
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-rose-400" />
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-amber-400" />
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-emerald-400" />
                  <span className="ml-1 sm:ml-2 text-[10px] sm:text-xs">
                    {t("landing.cta_primary").toLowerCase().includes("empieza")
                      ? "presupuesto.json"
                      : "pressupost.json"}
                  </span>
                </div>
                <pre className="text-slate-300 leading-relaxed overflow-x-auto whitespace-pre">
{`{
  "client": "${t("landing.demo_quote_client")}",
  "items": [
    {
      "description": "${t("landing.demo_json_desc_1")}",
      "amount": 4200,
      "vat": 21
    },
    {
      "description": "${t("landing.demo_json_desc_2")}",
      "amount": 2400,
      "vat": 21
    },
    {
      "description": "${t("landing.demo_json_desc_3")}",
      "amount": 1000,
      "vat": 21
    }
  ],
  "total": 9196.00 EUR
}`}
                </pre>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section
        id="features"
        className="relative z-10 max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-12 sm:py-20"
      >
        <div className="max-w-2xl mb-10 sm:mb-14">
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-blue-400">
            {t("landing.features_section_tag")}
          </span>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-bold tracking-tight sm:tracking-tighter mt-2">
            {t("landing.features_section_title")}
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
          {[
            { icon: Mic, k: "voice" },
            { icon: FileText, k: "pdf" },
            { icon: Building2, k: "multi" },
            { icon: ShieldCheck, k: "2fa" },
          ].map((f, i) => (
            <motion.div
              key={f.k}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="card-surface p-5 sm:p-7 hover:border-blue-500/30 transition-all group"
              data-testid={`feature-${f.k}`}
            >
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-blue-500/20 transition-all">
                <f.icon className="text-blue-400" size={18} />
              </div>
              <h3 className="font-display text-lg sm:text-xl font-semibold mb-1.5 sm:mb-2">
                {t(`landing.feature_${f.k}_title`)}
              </h3>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                {t(`landing.feature_${f.k}_desc`)}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-5xl mx-auto px-4 sm:px-5 lg:px-8 py-12 sm:py-20">
        <div className="card-surface p-6 sm:p-10 lg:p-14 text-center relative overflow-hidden">
          <div className="absolute inset-0 dot-grid opacity-30" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] sm:text-xs border border-blue-500/30 bg-blue-500/10 text-blue-300 mb-3 sm:mb-4">
              <Zap size={12} /> {t("landing.cta_section_badge")}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-bold tracking-tight sm:tracking-tighter break-words">
              {t("landing.cta_section_title")}
            </h2>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base text-slate-400 max-w-xl mx-auto">
              {t("landing.cta_section_sub")}
            </p>
            <div className="mt-6 sm:mt-8">
              <Link to={isLogged ? "/app" : "/register"}>
                <Button
                  data-testid="cta-final"
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-[0_0_30px_rgba(59,130,246,0.5)] px-6 sm:px-10 h-11 sm:h-12 whitespace-normal"
                >
                  {isLogged ? goAppLabel : t("landing.cta_section_btn")}
                  <ArrowRight size={16} className="ml-2 shrink-0" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 mt-6 sm:mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-5 lg:px-8 py-6 sm:py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-[11px] sm:text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <Cpu size={14} className="text-blue-400" />
            <span>© 2026 HO-SYS · hosys.es</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>Whisper · GPT-4o</span>
            <span className="hidden sm:inline">·</span>
            <span>{t("landing.footer_made")}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
